import type { FC } from 'react';
import { useRouter } from 'next/router';
import { useAuthState } from 'modules/Auth/AuthContext';
import { useEffect, useRef } from 'react';
import { useCurrentUserId } from 'modules/Auth/hooks';
import type { RolesT } from '../constants';

interface userOrgProp {
  userOrg: CF.API.Organizations.UserOrganization;
}

interface defaultConfig {
  fallbackTo?: string;
  restrictedRoles?: Array<RolesT>;
}

export function useCurrentUserOrg(): CF.API.Organizations.UserOrganization | undefined {
  const { authData } = useAuthState();
  const currentUserId = useCurrentUserId();
  return authData?.organizations?.find((userOrg) => userOrg.organization.id === currentUserId);
}

export function withUserOrganization<P>(config?: defaultConfig): (c: FC<P & userOrgProp>) => FC<P> {
  return (Component: FC<P & userOrgProp>): FC<P> =>
    (props: P) => {
      const router = useRouter();
      const userOrg = useCurrentUserOrg();
      const { fallbackTo = '/explore', restrictedRoles = [] } = config || {};
      const isRestricted = restrictedRoles.includes(userOrg?.role.id as RolesT);
      const isSettingsPage = /(^|\/)settings(\/|$)/.test(router.asPath);
      const userId = useCurrentUserId();
      const isMountedRef = useRef(true);
      const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

      // redirection happens if org account isn't selected or user's role is restricted
      useEffect(() => {
        if (isMountedRef.current && !userOrg && isSettingsPage) {
          router.push({
            pathname: '/[userOrOrgId]/settings',
            query: { userOrOrgId: userId },
          });
          isMountedRef.current = false;
        } else if (isMountedRef.current && (!userOrg || isRestricted)) {
          timeoutIdRef.current = setTimeout(() => {
            router.push(fallbackTo);
          }, 100);
        }
        return () => {
          if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
          }
          isMountedRef.current = false;
        };
      }, [userOrg, isRestricted]);

      return userOrg && !isRestricted ? <Component {...props} userOrg={userOrg} /> : null;
    };
}
